import { Close, KeyboardArrowDownRounded } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { MapFilterReportGeofenceList } from 'app/pages/Map';
import { useAsyncAction, useStateRef } from 'app/services';
import { geofenceGroupLoad } from 'app/store/map';
import { reportTypeLoad } from 'app/store/report';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { useSelector } from 'react-redux';

const MapFilterReportForm = forwardRef((props, ref) => {
  const theme = useTheme();
  const transportCheckedItems = useSelector((state) =>
    state.map.transportFilterCheckedType === 'filter' ? state.map.transportProcessingCheckedItems : state.map.transportCheckedItems
  );
  const { timezone } = useSelector((state) => state.session);
  const { typeItems } = useSelector((state) => state.report);
  const { geofenceItems } = useSelector((state) => state.map);

  const { asyncAction: fetchTypeData, isLoading: isFetchTypeLoading } = useAsyncAction(reportTypeLoad);
  const { asyncAction: fetchGroupData, isLoading: isFetchGroupLoading } = useAsyncAction(geofenceGroupLoad);
  const [geofenceSearchValue, setGeofenceSearchValue] = useState('');
  const [firstUpdate, setFirstUpdate, firstUpdateRef] = useStateRef(false);
  const { onSubmit } = props;
  const { control, watch, register, unregister, formState, setValue, handleSubmit, setError, clearErrors } = useForm({
    defaultValues: {
      type_id: '',
      sort: 'number',
      from: moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      to: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      timezone: timezone,
      geofence_ids: [],
    },
  });

  const typeIdWatch = watch('type_id');
  const fromWatch = watch('from');
  const toWatch = watch('to');
  const geofenceId = watch('geofence_ids');

  const filterTypes = useMemo(() => {
    return typeItems.filter((type) => type.method !== 'user_log');
  }, [typeItems]);

  const sortTypes = useMemo(() => {
    switch (typeIdWatch) {
      case 5:
        return [{ value: 'datetime', title: 'Начало рейса' }];
      default:
        return [
          { value: 'number', title: 'Госномер' },
          { value: 'datetime', title: 'Времени события' },
        ];
    }
  }, [typeIdWatch]);

  const geofenceSortItems = useMemo(() => {
    if (geofenceSearchValue.length) {
      return geofenceItems.filter((item) => item.name.toLowerCase().indexOf(geofenceSearchValue.toLowerCase()) !== -1);
    }

    return geofenceItems;
  }, [geofenceItems, geofenceSearchValue]);

  const handleChangeFromDatetime = (newValue) => {
    if (newValue.isValid()) {
      setValue('from', moment(newValue).format('YYYY-MM-DD HH:mm:ss'));
      clearErrors('from');
    } else {
      setError('from', { type: 'required' });
    }
  };

  const handleChangeToDatetime = (newValue, keyboardInputValue) => {
    if (newValue.isValid()) {
      setValue('to', moment(keyboardInputValue ? newValue : newValue.endOf('day')).format('YYYY-MM-DD HH:mm:ss'));
      clearErrors('to');
    } else {
      setError('to', { type: 'required' });
    }
  };

  const handleGeofenceInputChange = (value) => {
    setGeofenceSearchValue(value);
  };

  const handleGeofenceItemClick = (id) => {
    if (geofenceId.includes(id)) {
      setValue(
        'geofence_ids',
        geofenceId.filter((geofenceId) => geofenceId !== id)
      );
    } else {
      setValue('geofence_ids', [...geofenceId, id]);
    }
  };

  useImperativeHandle(ref, () => ({
    handleFormSubmit: () => {
      handleSubmit((data) =>
        onSubmit({
          type_id: parseInt(data.type_id),
          timezone: String(data.timezone),
          sort: data.sort,
          from: data.from,
          to: data.to,
          ids: transportCheckedItems.join(','),
          geofence_ids: data.geofence_ids ? data.geofence_ids : undefined,
          min_parking_time: data.min_parking_time ? data.min_parking_time : undefined,
          max_parking_distance: data.max_parking_distance ? data.max_parking_distance : undefined,
          geofence_known: data.geofence_known ? data.geofence_known : undefined,
          geofence_unknown: data.min_parking_time ? data.geofence_unknown : undefined,
          drain_diff: data.drain_diff ? data.drain_diff : undefined,
        })
      )();
    },
  }));

  useLayoutEffect(() => {
    if (firstUpdateRef.current !== undefined) {
      setTimeout(() => setFirstUpdate(undefined), theme.transitions.duration.shorter);
    }
  }, [firstUpdateRef.current]);

  useEffect(() => {
    switch (typeIdWatch) {
      case 1:
      case 3:
      case 5:
        unregister('min_parking_time');
        unregister('max_parking_distance');
        unregister('geofence_known');
        unregister('geofence_unknown');
        unregister('drain_diff');
        break;
      case 2:
        unregister('drain_diff');
        break;
      case 6:
        unregister('min_parking_time');
        unregister('max_parking_distance');
        unregister('geofence_known');
        unregister('geofence_unknown');
        break;
    }
  }, [typeIdWatch]);

  useEffect(() => {
    switch (typeIdWatch) {
      case 2:
        setValue('geofence_ids', geofenceId?.join(','));
        break;
      case 5:
        setValue('sort', 'datetime');
        break;
    }
  }, [typeIdWatch]);

  useEffect(() => {
    fetchTypeData();
    fetchGroupData();
  }, []);

  return (
    <form>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.type_id} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Тип отчета</InputLabel>
            {isFetchTypeLoading && (
              <OutlinedInput
                disabled
                endAdornment={
                  <InputAdornment position='end'>
                    <CircularProgress size={16} />
                  </InputAdornment>
                }
              />
            )}
            {!isFetchTypeLoading && (
              <Controller
                name='type_id'
                control={control}
                render={({ field }) => (
                  <Select
                    label='Тип отчета'
                    IconComponent={KeyboardArrowDownRounded}
                    MenuProps={{
                      sx: {
                        '.MuiPaper-root:before': {
                          display: 'none',
                        },
                      },
                    }}
                    {...register(field.name, {
                      required: true,
                      pattern: /^([0-9]{1,11})?$/iu,
                    })}
                    notched={firstUpdate}
                    value={field.value}
                    onChange={field.onChange}>
                    {filterTypes.map((type, key) => (
                      <MenuItem key={key} disableRipple value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}></Controller>
            )}
            <FormHelperText>Выберите тип отчета для формирования документа</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.sort} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Сортировка</InputLabel>
            <Controller
              name='sort'
              control={control}
              render={({ field }) => (
                <Select
                  label='Сортировка'
                  IconComponent={KeyboardArrowDownRounded}
                  MenuProps={{
                    sx: {
                      '.MuiPaper-root:before': {
                        display: 'none',
                      },
                    },
                  }}
                  {...register(field.name, {
                    required: true,
                  })}
                  notched={firstUpdate}
                  value={field.value}
                  onChange={field.onChange}>
                  {sortTypes.map((type, key) => (
                    <MenuItem key={key} disableRipple value={type.value}>
                      {type.title}
                    </MenuItem>
                  ))}
                </Select>
              )}></Controller>
            <FormHelperText>Выберите условия сортировки данных для формирования документа</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={moment.locale('ru')}>
          <Grid item xs={4.8}>
            <FormControl error={!!formState.errors.from} margin='normal' variant='outlined' fullWidth>
              <DesktopDatePicker
                label='Начало'
                views={['day']}
                minDate={moment().subtract(180, 'days')}
                maxDate={moment()}
                inputFormat='DD.MM.YYYY HH:mm:ss'
                value={fromWatch}
                onChange={handleChangeFromDatetime}
                renderInput={({ inputRef, inputProps, ...rest }) => (
                  <TextField
                    inputRef={inputRef}
                    inputProps={inputProps}
                    {...rest}
                    error={!!formState.errors.from}
                    sx={{
                      svg: {
                        fontSize: '20px',
                      },
                    }}
                  />
                )}
              />
              <FormHelperText>Начало формирования документа</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={4.7}>
            <FormControl error={!!formState.errors.to} margin='normal' variant='outlined' fullWidth>
              <DesktopDatePicker
                label='Конец'
                views={['day']}
                minDate={fromWatch}
                maxDate={moment()}
                inputFormat='DD.MM.YYYY HH:mm:ss'
                value={toWatch}
                onChange={handleChangeToDatetime}
                renderInput={({ inputRef, inputProps, ...rest }) => (
                  <TextField
                    inputRef={inputRef}
                    inputProps={inputProps}
                    {...rest}
                    error={!!formState.errors.to}
                    sx={{
                      svg: {
                        fontSize: '20px',
                      },
                    }}
                  />
                )}
              />
              <FormHelperText>Конец формирования документа</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={2.5}>
            <FormControl error={!!formState.errors.timezone} margin='normal' variant='outlined' fullWidth>
              <InputLabel disableAnimation={firstUpdate}>Часовой пояс</InputLabel>
              <Controller
                name='timezone'
                control={control}
                render={({ field }) => (
                  <Select
                    label='Часовой пояс'
                    IconComponent={KeyboardArrowDownRounded}
                    MenuProps={{
                      sx: {
                        '.MuiPaper-root:before': {
                          display: 'none',
                        },
                      },
                    }}
                    {...register(field.name, {
                      required: true,
                      pattern: /^[\d ()+-]{0,4}$/iu,
                    })}
                    notched={firstUpdate}
                    value={field.value}
                    onChange={field.onChange}>
                    {config.timezoneOffsets.map((timezone, key) => (
                      <MenuItem key={key} disableRipple value={timezone.value}>
                        {timezone.title}
                      </MenuItem>
                    ))}
                  </Select>
                )}></Controller>
            </FormControl>
          </Grid>
        </LocalizationProvider>
      </Grid>
      {typeIdWatch === 6 && (
        <React.Fragment>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl error={!!formState.errors.drain_diff} margin='normal' variant='outlined' fullWidth>
                <InputLabel disableAnimation={firstUpdate}>Разница во времени между сливами</InputLabel>
                <InputMask
                  mask='9999'
                  maskChar=''
                  defaultValue={120}
                  {...register('drain_diff', {
                    pattern: /^([0-9]{1,4})$/iu,
                    required: true,
                    valueAsNumber: true,
                  })}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Разница во времени между сливами'
                      variant='outlined'
                      notched={firstUpdate}
                      fullWidth
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
                <FormHelperText>Укажите разницу во времени между сливами в секундах</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </React.Fragment>
      )}
      {typeIdWatch === 2 && (
        <React.Fragment>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl error={!!formState.errors.min_parking_time} margin='normal' variant='outlined' fullWidth>
                <InputLabel disableAnimation={firstUpdate}>Минимальное время стоянки</InputLabel>
                <InputMask
                  mask='9999'
                  maskChar=''
                  defaultValue={300}
                  {...register('min_parking_time', {
                    pattern: /^([0-9]{1,4})$/iu,
                    required: true,
                    valueAsNumber: true,
                  })}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Минимальное время стоянки'
                      variant='outlined'
                      notched={firstUpdate}
                      fullWidth
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
                <FormHelperText>Укажите минимальное время стоянки в секундах</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl error={!!formState.errors.max_parking_distance} margin='normal' variant='outlined' fullWidth>
                <InputLabel disableAnimation={firstUpdate}>Максимальное расстояние между стоянками</InputLabel>
                <InputMask
                  mask='9999'
                  maskChar=''
                  defaultValue={500}
                  {...register('max_parking_distance', {
                    pattern: /^([0-9]{1,4})$/iu,
                    required: true,
                    valueAsNumber: true,
                  })}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Максимальное расстояние между стоянками'
                      variant='outlined'
                      notched={firstUpdate}
                      fullWidth
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
                <FormHelperText>Укажите максимальное расстояние между стоянками время стоянки в метрах</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl
                margin='normal'
                variant='outlined'
                fullWidth
                sx={{
                  '.MuiInputLabel-root.MuiInputLabel-shrink': {
                    color: 'text.secondary',
                  },
                }}>
                <InputLabel disableAnimation={firstUpdate} shrink>
                  Геозоны
                </InputLabel>
                {isFetchGroupLoading && (
                  <OutlinedInput
                    disabled
                    notched
                    label='Геозоны'
                    variant='outlined'
                    fullWidth
                    endAdornment={
                      <InputAdornment position='end'>
                        <CircularProgress size={16} />
                      </InputAdornment>
                    }
                  />
                )}
                {!isFetchGroupLoading && (
                  <React.Fragment>
                    <OutlinedInput
                      label='Геозоны'
                      variant='outlined'
                      placeholder='Название геозоны'
                      notched
                      value={geofenceSearchValue}
                      onChange={(e) => handleGeofenceInputChange(e.target.value)}
                      fullWidth
                      endAdornment={
                        <InputAdornment position='end'>
                          <IconButton disableRipple disableTouchRipple onClick={() => handleGeofenceInputChange('')}>
                            <Close fontSize='small' />
                          </IconButton>
                        </InputAdornment>
                      }
                      sx={{
                        '.MuiOutlinedInput-notchedOutline': {
                          borderBottomRightRadius: '0',
                          borderBottomLeftRadius: '0',
                          borderColor: '#d5d5d5!important',
                        },
                        '.MuiInputAdornment-positionEnd': {
                          display: 'none',
                          '.MuiButtonBase-root': {
                            padding: theme.spacing(0),
                            margin: theme.spacing(0),
                            svg: {
                              fontSize: '16px',
                            },
                          },
                          ...(geofenceSearchValue.length !== 0 && {
                            display: 'flex',
                          }),
                        },
                      }}
                    />
                    <Box
                      component='div'
                      sx={(theme) => ({
                        width: '100%',
                        height: '180px',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: '#d5d5d5',
                        borderRadius: theme.shape.borderRadius,
                        borderTop: '0',
                        borderTopRightRadius: '0',
                        borderTopLeftRadius: '0',
                      })}>
                      {geofenceSortItems.length !== 0 && (
                        <MapFilterReportGeofenceList items={geofenceSortItems} checkedId={geofenceId} onClick={handleGeofenceItemClick} />
                      )}
                      {geofenceSortItems.length === 0 && (
                        <Stack
                          direction='column'
                          alignItems='center'
                          justifyContent='center'
                          sx={(theme) => ({
                            width: '100%',
                            height: '100%',
                            padding: theme.spacing(2),
                          })}>
                          <Typography align='center' variant='body1' component='span'>
                            Ничего не найдено
                          </Typography>
                          <Typography align='center' color='text.secondary' variant='caption' component='span'>
                            Ни одна геозона не соответствует заданным параметрам
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  </React.Fragment>
                )}
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sx={{
                '.MuiCheckbox-root': {
                  padding: '0 9px',
                  '&:not(.Mui-checked) svg': {
                    color: '#d5d5d5',
                  },
                },
              }}>
              <FormControlLabel
                label='Нахождение только в известных зонах'
                control={
                  <Controller
                    name='geofence_known'
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <Checkbox
                        size='small'
                        disableRipple
                        disabled={!!geofenceId.length}
                        checked={!!field.value}
                        onChange={field.onChange}
                        {...register(field.name)}
                      />
                    )}></Controller>
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                '.MuiCheckbox-root': {
                  padding: '0 9px',
                  '&:not(.Mui-checked) svg': {
                    color: '#d5d5d5',
                  },
                },
              }}>
              <FormControlLabel
                label='Стоянки вне известных зон'
                control={
                  <Controller
                    name='geofence_unknown'
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <Checkbox
                        size='small'
                        disableRipple
                        disabled={!!geofenceId.length}
                        checked={!!field.value}
                        onChange={field.onChange}
                        {...register(field.name)}
                      />
                    )}></Controller>
                }
              />
            </Grid>
          </Grid>
        </React.Fragment>
      )}
    </form>
  );
});

export default MapFilterReportForm;
